<script setup lang="ts">
import Translator from "@/utils/common/Translator.ts";
import EditViewBlock from "@/components/EditView/EditViewBlock.vue";
import {QDialog, QTabPanel, useDialogPluginComponent} from "quasar";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {onBeforeMount, ref} from "vue";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import ViewMode from "@/utils/constants/ViewMode.ts";
import EditViewSkeleton from "@/components/skeleton/EditViewSkeleton.vue";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import DialogButtons from "@/utils/forms/DialogButtons.ts";
import {useLoginInfoStore} from "@/stores/loginInfo.ts";
import EditPlace from "@/utils/presenters/EditPlace.ts";
import {AxiosRequestConfig} from "axios";
import {useMessagesStore} from "@/stores/ToastDataStore.ts";
import {ActionPlaceholder} from "@/utils/moreMenu/ActionHandler.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import BaseEntityDTO = namaTypes.BaseEntityDTO;
import DTOGui2Response = namaTypes.DTOGui2Response;
import EditScreenGroup = namaTypes.EditScreenGroup;
import DTOBulkEditField = namaTypes.DTOBulkEditField;

const props = defineProps<{ editPlace: EditPlace, bulkEditFields: DTOBulkEditField[], selectedRowIds: string[], action: ActionPlaceholder }>();
const {compactMode, darkMode} = storeToRefs(useNamaFlagsStore());
const layoutFetched = ref(false);
const {dialogRef, onDialogCancel} = useDialogPluginComponent()
const loginStore = useLoginInfoStore();
const object = ref<BaseEntityDTO>({});
const currentPageIndex = ref(0);

function onPageChanged(newValue: number) {
  currentPageIndex.value = newValue;
}

const quickCreatePlace: EditPlace = props.editPlace;
quickCreatePlace.quickCreate = true;
quickCreatePlace.view = ViewMode.EDIT;
const presenter: EditPresenter = new EditPresenter(quickCreatePlace);
const entityType: string | undefined = presenter.entityType
presenter.closeQuickEditDialog = onDialogCancel;
const bulkEditBlock = ref<EditScreenGroup>({
  title: {resourceId: "bulkEdit"},
  editScreenBlock: true,
  fields: [],
  namaClassTypeId: "EditScreenGroup",
  iconCode: props.action.iconCode ?? "nm-edit"
})

onBeforeMount(async () => {
  if (quickCreatePlace.view === ViewMode.EDIT) {
    const result = await NamaCommon.namaFetch(NamaURLS.FIND_BY_ID, {
      params: {
        entityType: presenter.entityType,
        id: presenter.place.id
      }
    });
    if (result && NamaCommon.isSuccess(result))
      object.value = result.data;
  }
  if (object.value && object.value.commitedBefore) {
    presenter.place.view = ViewMode.EDIT;
    presenter.place.id = object.value.id;
    presenter.place.createdObjectId = object.value.id;
  }
  presenter.createdObject = object.value;
  quickCreatePlace.quickCreateContextInfo = null;
  await presenter.start();
  presenter.afterFetchListener = () => {
  };
  addFields(presenter)
});

function addFields(presenter: EditPresenter) {
  presenter.pages.value = [{title: {resourceId: "mainPage"}, blocks: [bulkEditBlock.value]}];
  for (const field of props.bulkEditFields) {
    bulkEditBlock.value.fields.push({id: field.fieldId, layout: field.fieldLayout})
  }
  layoutFetched.value = true
}

async function onOkClick() {
  let shouldClose = await saveRecordAndSelectedIfNeeded();
  if (shouldClose)
    onDialogCancel()
}

async function saveRecordAndSelectedIfNeeded(): Promise<boolean> {
  let settings: AxiosRequestConfig = {
    params: {
      entityType: props.editPlace.entityType
    },
    data: {
      selectedRowIds: props.selectedRowIds,
      record: presenter.fetchRecord(),
      editedFields: props.bulkEditFields.map(b => b.fieldId)
    }
  }

  let response: DTOGui2Response = await NamaCommon.namaFetch(NamaURLS.BULK_EDIT_AND_SAVE, settings);
  if (!NamaCommon.isSuccess(response))
    return false;
  useMessagesStore().showSuccessMessage();
  return true;
}

const bulkEditDialogTitle =
  Translator.translateId('bulkEdit') + " - " +
  Translator.translateId('rowsCount') + " (" + props.selectedRowIds.length + ") - " +
  Translator.translateId(entityType);

</script>

<template>
  <q-dialog @onHide="()=>presenter.closeQuickEditDialog=undefined" persistent ref="dialogRef" @hide="onDialogCancel" v-if="loginStore.isLoggedIn"
            full-height full-width @keydown.esc="onDialogCancel">
    <q-card
      class="tw-flex tw-flex-col tw-w-full tw-h-full tw-shadow-none tw-bg-namaBackground-bright-light dark:tw-text-white dark:tw-bg-namaBackground-bright-dark">
      <q-bar
        class="tw-flex-none tw-h-fit tw-min-h-8 tw-text-namaFontColor-input-light tw-bg-[#CED4DA] tw-px-2 tw-py-[6px] tw-flex tw-justify-between dark:tw-bg-[#212529] dark:tw-text-white">
        <div v-text="bulkEditDialogTitle"/>
        <q-btn dense flat @click="onDialogCancel" :title="Translator.translateId( 'close')" class="tw-h-fit">
          <NamaIcon default-icon="nm-win-close"/>
        </q-btn>
      </q-bar>
      <q-card-section class="tw-grow tw-flex tw-flex-col compact:tw-p-0 ultra:tw-p-0">
        <div class="tw-grow tw-bg-namaBackground-bright-light tw-rounded dark:tw-bg-namaBackground-bright-dark">
          <div class="tw-h-full tw-flex tw-flex-col compact:tw-pt-2 ultra:tw-pt-1 compact:tw-p-3 ultra:tw-p-1.5">
            <template v-if="presenter.validLayout && layoutFetched">
              <div
                class="tw-flex-none tw-h-fit tw-top-0 tw-rounded-b-lg tw-bg-namaBackground-deep-light dark:tw-bg-namaBackground-deep-dark tw-pb-0 compact:tw-px-0.5 ultra:tw-px-0">
                <q-tabs
                  :model-value="currentPageIndex"
                  @update:model-value="onPageChanged"
                  content-class="edit-view-tab-content tw-text-namaFontColor-header-light dark:tw-text-namaFontColor-header-dark tw-pt-2 tw-px-2.5 compact:tw-px-1 ultra:tw-px-0.5 mobile:tw-px-2.5"
                  indicator-color="primary"
                  align="left"
                  :dense="compactMode"
                  narrow-indicator
                  :no-caps="true">
                  <q-separator v-if="!compactMode"/>
                  <q-tab
                    v-for="(page, pageIndex) in presenter.pages.value" :key="pageIndex"
                    :name="pageIndex" :label="Translator.translateNaMaText(page.title, presenter.entityType)"/>
                </q-tabs>
              </div>
              <q-tab-panels keep-alive animated v-model="currentPageIndex"
                            class="tw-grow tw-bg-namaBackground-bright-light tw-rounded-lg dark:tw-bg-namaBackground-bright-dark">
                <q-tab-panel ref="qTabPanelRef" v-for="(page, pageIndex) in presenter.pages.value" :key="pageIndex" :name="pageIndex"
                             class="tw-h-full tw-p-0 tw-pt-4 compact:tw-p-0 ultra:tw-p-0 compact:tw-pt-1 ultra:tw-pt-0.5">
                  <q-scroll-area :dir="Translator.LTR" :dark="darkMode" class="tw-h-full">
                    <div :dir="Translator.fetchDir()" class="tw-flex tw-flex-wrap tw-gap-2.5 compact:tw-gap-1 ultra:tw-gap-0.5">
                      <template v-for="(block, blockIndex) in page.blocks">
                        <EditViewBlock :block="block" :presenter="presenter" :blockIndex="blockIndex"
                                       :pageIndex="pageIndex" :showMainFile="false"/>
                      </template>
                    </div>
                  </q-scroll-area>
                </q-tab-panel>
              </q-tab-panels>
            </template>
            <template v-else>
              <EditViewSkeleton/>
            </template>
          </div>
        </div>
      </q-card-section>
      <q-card-section class="tw-flex-none tw-h-fit tw-p-0 tw-pb-4 compact:tw-pb-2 ultra:tw-pb-1 tw-flex tw-flex-row tw-gap-4 tw-justify-center">
        <q-btn
          :label="Translator.translateId(DialogButtons.Ok)"
          @click="onOkClick"
          @keydown.enter="onOkClick"
          :dense="compactMode"
          class="tw-h-10 tw-bg-primary-light dark:tw-bg-primary-dark tw-text-white tw-text-base tw-font-normal tw-rounded-md tw-min-w-28 tw-max-w-fit"/>
        <q-btn
          outline
          :label="Translator.translateId(DialogButtons.Cancel)"
          @click="onDialogCancel"
          :dense="compactMode"
          class="tw-h-10 tw-text-namaGray dark:tw-text-white tw-text-base tw-font-normal tw-rounded-md tw-min-w-28 tw-max-w-fit"/>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>
